import store from '@/vuex/store'
/**
 * The Directive syntax will look like this:
 *    <CheatsheetToggleButton v-aha-emit-action>Click on this</CheatsheetToggleButton>
 *    The directive will dispatch a vuex action called 'user_action'
 *    The payload should contain:
 *    event + element + vueVirtualElement (vnode)
 */

const CLICK_EVENT = 'click'
const HOVER_EVENT = 'mouseenter'
const DOUBLE_CLICK_EVENT = 'dblclick'
const FOCUS_EVENT = 'focus'
const BLUR_EVENT = 'blur'
const CHANGE_EVENT = 'change'
const FORM_SUBMIT_EVENT = 'submit'
// Also support multiple events
const SUPPORTED_EVENTS = [CLICK_EVENT, HOVER_EVENT, DOUBLE_CLICK_EVENT,
  FOCUS_EVENT, BLUR_EVENT, CHANGE_EVENT, FORM_SUBMIT_EVENT]

function _getCallbackKey(event) {
  return `aha-emit-action-${event}-callback`
}

export default {
  bind(el, binding, vnode) {
    // If the view modifier is present, we will use the IntersectionObserver API
    // Only to observe the element if it enters the viewport and do nothing more
    if (binding.modifiers && binding.modifiers.view) {
      const _callback = (entries) => {
        entries.forEach((entry) => {
          // Workaround for the case when the element is inside a modal
          // Because it is put inside v-portals, the IntersectionObserver never triggers
          const isModal = entry.target.classList.contains('v-portal')
          if (!entry.isIntersecting && !isModal) return
          store.dispatch('userAction', {
            eventName: 'view',
            el,
            binding,
            vnode,
          })
        })
      }

      const showInViewObserver = new IntersectionObserver(_callback, {
        root: null, // Use the viewport as the root
        rootMargin: '0px', // No margin around the root
        threshold: 0.1, // 10% of the element must be visible to trigger the callback
      })
      showInViewObserver.observe(el)
      el.showInViewObserver = showInViewObserver
      return
    }

    SUPPORTED_EVENTS.forEach((eventName) => {
      function callback(e) {
        // dispatch vuex action
        // The payload should contain:
        // eventName + element + vueVirtualElement (vnode)
        if (eventName === HOVER_EVENT && e.target !== el) return

        // When there's a change in the input/ textarea,
        // we add e.target.value to payload.binding.value.user_input.
        if (eventName === CHANGE_EVENT && e.target && e.target.value) {
          binding.value = { ...binding.value, user_input: e.target.value }
        }

        store.dispatch('userAction', {
          eventName,
          el,
          binding,
          vnode,
        })
      }

      el.addEventListener(eventName, callback, true)
      el[_getCallbackKey(eventName)] = callback
    })
  },

  unbind(el, binding) {
    // If the view modifier is present, we will use the IntersectionObserver API
    // Only to observe the element if it enters the viewport and do nothing more
    if (binding.modifiers && binding.modifiers.view && el.showInViewObserver) {
      const observer = el.showInViewObserver
      observer.unobserve(el)
      observer.disconnect()
      return
    }

    SUPPORTED_EVENTS.forEach((event) => {
      el.removeEventListener(event, el[_getCallbackKey(event)], true)
    })
  },
}
